import { useState, useEffect } from 'react'
import { Card, Typography, Button, Input, Space, message, Alert, Popconfirm, Descriptions } from 'antd'
import { CopyOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined, PlusOutlined, ApiOutlined } from '@ant-design/icons'
import { Link, useLocation } from 'react-router-dom'
import { regenerateApiKey, getMerchant, createOnboardingFeeCheckout, updateMerchant as updateMerchantSettings } from '../../services/merchant'
import { useMerchantStore } from '../../stores/merchantStore'

const { Title, Text, Paragraph } = Typography
const EPAY_API_URL = `${(import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '')}/epay`

export default function ApiKeys() {
  const location = useLocation()
  const [liveKey, setLiveKey] = useState<string | null>(null)
  const [showLive, setShowLive] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [payingOnboardingFee, setPayingOnboardingFee] = useState(false)
  const [savingWhitelist, setSavingWhitelist] = useState(false)
  const [totpCode, setTotpCode] = useState('')
  const [apiIpWhitelistInput, setApiIpWhitelistInput] = useState('')
  const { merchant, setMerchant } = useMerchantStore()

  useEffect(() => {
    getMerchant().then((res) => setMerchant(res.data))
  }, [setMerchant, location.search])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    if (searchParams.get('onboardingPaid') === '1') {
      message.success('开户费支付完成，正在刷新商户状态')
      getMerchant().then((res) => setMerchant(res.data))
    }
  }, [location.search, setMerchant])

  useEffect(() => {
    setApiIpWhitelistInput((merchant?.apiIpWhitelist ?? []).join(', '))
  }, [merchant?.apiIpWhitelist])

  const hasKey = merchant?.hasLiveApiKey || liveKey !== null
  const onboardingBlocked = !hasKey && Boolean(merchant?.onboardingFeeRequired)
  const missingProfileFields = !hasKey && merchant ? [
    !merchant.website?.trim() ? '網站' : null,
    !merchant.description?.trim() ? '商戶描述' : null,
    !merchant.phone?.trim() ? '電話號碼' : null,
  ].filter((field): field is string => field !== null) : []
  const profileBlocked = missingProfileFields.length > 0

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success('已複製到剪貼板')
  }

  const handleGenerate = async () => {
    setRegenerating(true)
    try {
      const res = await regenerateApiKey(totpCode || undefined)
      setLiveKey(res.data.apiKey)
      setShowLive(true)
      getMerchant().then((r) => setMerchant(r.data))
      message.success(hasKey ? 'API Key 已重新生成' : 'API Key 已生成')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '操作失敗')
    } finally {
      setRegenerating(false)
    }
  }

  const handlePayOnboardingFee = async () => {
    setPayingOnboardingFee(true)
    try {
      const current = `${window.location.origin}/dashboard/developers/api-keys?onboardingPaid=1`
      const res = await createOnboardingFeeCheckout({
        successUrl: current,
        cancelUrl: current,
      })
      window.location.href = res.data.checkoutUrl
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '创建开户费支付失败')
    } finally {
      setPayingOnboardingFee(false)
    }
  }

  const handleSaveApiIpWhitelist = async () => {
    setSavingWhitelist(true)
    try {
      const entries = apiIpWhitelistInput
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
      const res = await updateMerchantSettings({ apiIpWhitelist: entries })
      setMerchant(res.data)
      setApiIpWhitelistInput(res.data.apiIpWhitelist.join(', '))
      message.success(entries.length > 0 ? 'API IP 白名单已保存' : '已清空 API IP 白名单，允许所有 IP')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '保存失败')
    } finally {
      setSavingWhitelist(false)
    }
  }

  const generateButton = (
    <Button
      icon={hasKey ? <ReloadOutlined /> : <PlusOutlined />}
      loading={regenerating}
      onClick={hasKey ? undefined : handleGenerate}
      disabled={onboardingBlocked || profileBlocked}
    >
      {hasKey ? '重新生成' : '生成'}
    </Button>
  )

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>API Keys</Title>

      <Alert
        type="warning"
        message="請妥善保管您的 API Key"
        description="API Key 只會在生成時顯示一次。如果遺失，請重新生成。"
        style={{ marginBottom: 24 }}
        showIcon
      />
      {onboardingBlocked && (
        <Alert
          type="error"
          message="生成 API Key 前需先完成開戶費"
          description={(
            <Space direction="vertical" size={8}>
              <span>
                当前开户费为 ${merchant?.onboardingFeeAmountUsd ?? '0'} USD。若你通过代理邀请码注册，可联系管理员确认专属开户费金额。
              </span>
              <Button
                type="primary"
                loading={payingOnboardingFee}
                onClick={handlePayOnboardingFee}
              >
                立即支付开户费
              </Button>
            </Space>
          )}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}
      {profileBlocked && (
        <Alert
          type="warning"
          message="生成 API Key 前需完成商戶資訊"
          description={<span>請填寫{missingProfileFields.join('、')}。<Link to="/dashboard/settings">前往帳戶設定</Link></span>}
          style={{ marginBottom: 24 }}
          showIcon
        />
      )}

      {hasKey && merchant && (
        <Card
          title={<><ApiOutlined style={{ marginRight: 8 }} />易支付協議兼容接入</>}
          style={{ marginBottom: 24 }}
        >
          <Paragraph>
            本平台兼容<Text strong>易支付（YiZhifu）API 協議</Text>，如果您的系統已對接易支付，只需將接口地址切換到本平台即可完成遷移，無需修改其他代碼。
          </Paragraph>
          <Descriptions column={1} size="small" bordered style={{ marginBottom: 12 }}>
            <Descriptions.Item label="API 地址">
              <Text code copyable>{EPAY_API_URL}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="商戶 PID">
              <Text code copyable>{merchant.id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="商戶密鑰 (Key)">
              即下方的 Live Mode API Key
            </Descriptions.Item>
          </Descriptions>
          <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 13 }}>
            支持的端點：submit.php（頁面跳轉）、mapi.php（API 模式）、api.php（訂單查詢）。詳情請參閱易支付接口文檔。
          </Paragraph>
        </Card>
      )}

      <Card title="Live Mode API Key" extra={<Text type="success">生產環境</Text>}>
        <Paragraph>用於生產環境的 API Key，交易將產生真實費用。</Paragraph>
        {merchant?.twoFactorEnabled && (
          <Input
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value)}
            maxLength={6}
            placeholder="請輸入 2FA 驗證碼"
            style={{ marginBottom: 16 }}
          />
        )}
        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={liveKey ? (showLive ? liveKey : '••••••••••••••••••••••••') : (hasKey ? 'kyren_live_••••••••••••••••' : '')}
            readOnly
            placeholder={hasKey ? undefined : '尚未生成 API Key'}
            style={{ fontFamily: 'monospace' }}
          />
          {liveKey && (
            <Button icon={showLive ? <EyeInvisibleOutlined /> : <EyeOutlined />} onClick={() => setShowLive(!showLive)} />
          )}
          {liveKey && <Button icon={<CopyOutlined />} onClick={() => handleCopy(liveKey)} />}
          {hasKey ? (
            <Popconfirm title="確定要重新生成 Live API Key 嗎？舊的 Key 將失效。" onConfirm={handleGenerate}>
              {generateButton}
            </Popconfirm>
          ) : (
            generateButton
          )}
        </Space.Compact>

        <div style={{ marginTop: 20 }}>
          <Text strong>API IP 白名单</Text>
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 12 }}>
            支持多个 IP 地址，使用英文逗号分隔。留空表示允许所有 IP 地址请求 API 接口。
          </Paragraph>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Input.TextArea
              value={apiIpWhitelistInput}
              onChange={(event) => setApiIpWhitelistInput(event.target.value)}
              placeholder="例如：1.1.1.1, 8.8.8.8, 2001:db8::1"
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
            <Button type="primary" loading={savingWhitelist} onClick={handleSaveApiIpWhitelist}>
              保存白名单
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  )
}
